import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getAllCompanies } from '../queries/companies';
import { getAllProducts } from '../queries/products';
import {
  getProspectSubmissions,
  useCreateProspectSubmission,
  useDeleteProspectSubmission,
  useUpdateProspectSubmission,
} from '../queries/prospectSubmissions';
import ProspectsShell from '../components/Prospects/ProspectsShell';
import ProspectsEmptyState from '../components/Prospects/ProspectsEmptyState';
import AddProspectModal from '../components/Prospects/AddProspectModal';
import ProspectDetailsModal from '../components/Prospects/ProspectDetailsModal';
import ProspectStatusChip from '../components/Prospects/ProspectStatusChip';
import { PageActionButton } from '../components/PageShell';
import {
  formatSubmittedDate,
  prospectErrorMessage,
  tableCellSx,
  tableHeadCellSx,
} from '../components/Prospects/prospectFields';

const SUBTITLE = 'Add prospects and track their approval status with clients.';

const LeadAgentProspectsPage = () => {
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const { data: companies } = useQuery(getAllCompanies({}));
  const { data: products } = useQuery(getAllProducts({}));
  const { data: prospects, isLoading, isError } = useQuery(getProspectSubmissions());
  const { mutate: createProspect, isPending: isCreating } = useCreateProspectSubmission();
  const { mutate: updateProspect, isPending: isUpdating } = useUpdateProspectSubmission();
  const { mutate: deleteProspect, isPending: isDeleting } = useDeleteProspectSubmission();

  const rows = prospects || [];
  const isEmpty = !isLoading && !isError && rows.length === 0;

  const handleCreate = (values) => {
    createProspect(
      { data: values },
      {
        onSuccess: () => {
          toast('Prospect submitted for approval.', { type: 'success' });
          setAddOpen(false);
        },
        onError: (error) => {
          toast(prospectErrorMessage(error, 'Could not submit this prospect.'), { type: 'error' });
        },
      },
    );
  };

  const handleSave = (values, { onSuccess } = {}) => {
    if (!selected?._id) return;
    updateProspect(
      { id: selected._id, data: values },
      {
        onSuccess: () => {
          toast('Prospect saved.', { type: 'success' });
          onSuccess?.();
          setSelected(null);
        },
        onError: (error) => {
          toast(prospectErrorMessage(error, 'Could not save this prospect.'), { type: 'error' });
        },
      },
    );
  };

  const confirmDelete = () => {
    const row = deleteRow;
    if (!row) return;
    deleteProspect(
      { id: row._id },
      {
        onSuccess: () => {
          toast('Prospect deleted.', { type: 'success' });
          setDeleteRow(null);
          if (selected?._id === row._id) setSelected(null);
        },
        onError: (error) => {
          setDeleteRow(null);
          toast(prospectErrorMessage(error, 'Could not delete this prospect.'), { type: 'error' });
        },
      },
    );
  };

  const addButton = <PageActionButton onClick={() => setAddOpen(true)}>Add prospect</PageActionButton>;

  return (
    <ProspectsShell title='Prospects' subtitle={SUBTITLE} action={addButton}>
      {isLoading ? (
        <Box display='flex' justifyContent='center' py={10}>
          <CircularProgress />
        </Box>
      ) : null}
      {isError ? (
        <Typography color='error' align='center'>
          Could not load prospects.
        </Typography>
      ) : null}
      {isEmpty ? (
        <ProspectsEmptyState
          title='No prospects yet'
          description='Add your first prospect to get started. Once a prospect is approved by the client, you can use Engage.'
          actionLabel='Add prospect'
          onAction={() => setAddOpen(true)}
        />
      ) : null}
      {!isLoading && !isError && rows.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Company', 'Contact name', 'Title', 'Client', 'Date submitted', 'Status', 'Edit', 'Delete'].map(
                  (heading) => (
                    <TableCell key={heading} sx={tableHeadCellSx}>
                      {heading}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row._id} sx={{ '& > *': { py: 1.5 } }}>
                  <TableCell sx={tableCellSx}>{row.company}</TableCell>
                  <TableCell sx={tableCellSx}>{row.contactName}</TableCell>
                  <TableCell sx={tableCellSx}>{row.title || '—'}</TableCell>
                  <TableCell sx={tableCellSx}>{row.client?.companyName || '—'}</TableCell>
                  <TableCell sx={tableCellSx}>{formatSubmittedDate(row.submittedAt)}</TableCell>
                  <TableCell sx={tableCellSx}>
                    <ProspectStatusChip status={row.status} />
                  </TableCell>
                  <TableCell sx={tableCellSx}>
                    <IconButton aria-label='Edit' onClick={() => setSelected(row)}>
                      <EditIcon color='primary' />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={tableCellSx}>
                    {row.canDelete ? (
                      <IconButton aria-label='Delete' disabled={isDeleting} onClick={() => setDeleteRow(row)}>
                        <DeleteIcon color='error' />
                      </IconButton>
                    ) : (
                      <Tooltip title='Only pending prospects that were never approved can be deleted.' arrow>
                        <span>
                          <IconButton aria-label='Delete' disabled>
                            <DeleteIcon color='disabled' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
      <AddProspectModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleCreate}
        isSubmitting={isCreating}
        companies={companies}
        products={products}
      />
      <ProspectDetailsModal
        open={Boolean(selected)}
        prospect={selected}
        onClose={() => setSelected(null)}
        onSave={handleSave}
        isSaving={isUpdating}
        companies={companies}
        products={products}
      />
      <Dialog open={deleteRow !== null} onClose={() => !isDeleting && setDeleteRow(null)}>
        <DialogTitle>Delete prospect?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} color='secondary' disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color='error' variant='contained' disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </ProspectsShell>
  );
};

export default LeadAgentProspectsPage;
